import { Component, EventEmitter, Inject, Output } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { NgClass } from '@angular/common'
import { SugestaoAgrupamentoService } from '../../../core/services/sugestao-agrupamento/sugestao-agrupamento.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { ModalMoverPassageiroComponent } from './modal-mover-passageiro.component'
import { ItemProposalCreateFormComponent } from '../../proposal/item-proposal-create/item-proposal-create-form/item-proposal-create-form.component'
import { ItemProposalEditFormComponent } from '../../proposal/item-proposal-edit/item-proposal-edit-form/item-proposal-edit-form.component'
import { ProposalItemService } from '../../../core/services/proposal/proposal-item/proposal-item.service'
import moment from 'moment'

type FlowState = 'idle' | 'generated' | 'review'

interface PassageiroUI {
  id: string
  nome: string
  hora: string
  speaker: boolean
  multiploTrecho: boolean
  repetida: boolean
}

interface GrupoUI {
  itemPropostaId: number
  itemPropostaOriginalId: number | null
  isDuplicado: boolean
  itemContratoId: string | null
  tipoVeiculoDescricao: string
  capacidade: number
  tipoTrechoId: number
  data: string
  horaInicio: string
  horaFinal: string
  voo: string | null
  totalPassageiros: number
  passageiros: PassageiroUI[]
}

@Component({
  selector: 'modal-sugestao-agrupamento',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatCheckboxModule,
    NgClass,
    ItemProposalEditFormComponent,
  ],
  templateUrl: './modal-sugestao-agrupamento.component.html',
  styleUrl: './modal-sugestao-agrupamento.component.scss',
})
export class ModalSugestaoAgrupamentoComponent {
  @Output() response = new EventEmitter<boolean>()

  state: FlowState = 'idle'
  janelaMinutos = 60
  agruparPorOrigem = true
  agruparPorDestino = true
  agruparPorVoo = false
  loading = false

  grupos: GrupoUI[] = []
  semCorrespondencia: any[] = []
  confirmedGroups: any[] = []
  podeAjustarValores = false

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalSugestaoAgrupamentoComponent>,
    private dialog: MatDialog,
    private sugestaoService: SugestaoAgrupamentoService,
    private proposalItemService: ProposalItemService,
    private snackbar: SnackbarCustomService,
  ) {}

  get contratoId(): any {
    return this.data?.id
  }

  gerar() {
    this.loading = true
    this.sugestaoService.gerar(this.contratoId, this.janelaMinutos, this.agruparPorOrigem, this.agruparPorDestino, this.agruparPorVoo).subscribe({
      next: (res: any) => {
        this.grupos = (res.data?.grupos ?? []).map((g: any) => ({
          ...g,
          isDuplicado: g.isDuplicado ?? false,
          itemPropostaOriginalId: g.itemPropostaOriginalId ?? null,
          itemContratoId: g.itemContratoId ?? null,
          passageiros: g.passageiros ?? [],
        }))
        this.semCorrespondencia = res.data?.semCorrespondencia ?? []
        this.state = 'generated'
        this.loading = false
      },
      error: () => {
        this.snackbar.error('Não foi possível gerar a sugestão.')
        this.loading = false
      },
    })
  }

  confirmar() {
    const grupos = this.grupos.map(g => ({
      itemPropostaId: g.itemPropostaId,
      passageiroIds: g.passageiros.map(p => p.id),
    }))
    this.loading = true
    this.sugestaoService.confirmar(this.contratoId, grupos).subscribe({
      next: (res: any) => {
        this.loading = false
        this.snackbar.success(res?.message || 'Agrupamento confirmado com sucesso!')
        const data = res?.data
        if (data?.podeAjustarValores) {
          this.confirmedGroups = data.grupos ?? []
          this.podeAjustarValores = true
          this.state = 'review'
        } else {
          this.response.emit(true)
          this.dialogRef.close()
        }
      },
      error: () => {
        this.snackbar.error('Não foi possível confirmar o agrupamento.')
        this.loading = false
      },
    })
  }

  editGrupo(grupo: any) {
    this.loading = true
    this.proposalItemService.getProposalItembyId(grupo.itemPropostaId).subscribe({
      next: (res: any) => {
        this.loading = false
        const proposalInfo = {
          statusPropostaId: this.data.statusPropostaId ?? 4,
          clienteId: this.data.idCliente ?? this.data.clienteId ?? 0,
          subClienteId: this.data.subClienteId ?? 0,
        }
        const dialogRef = this.dialog.open(ItemProposalEditFormComponent, {
          enterAnimationDuration: '100ms',
          exitAnimationDuration: '100ms',
          maxWidth: '100vw',
          width: '100vw',
          maxHeight: '100vh',
          height: '100vh',
          panelClass: 'dialog-fullscreen',
          data: { edit: true, item: res.data, proposalInfo, forceEditable: true },
        })
        dialogRef.componentInstance.response.subscribe(() => {
          this.snackbar.success('Item de proposta atualizado com sucesso!')
        })
      },
      error: () => {
        this.loading = false
        this.snackbar.error('Não foi possível carregar o item de proposta.')
      },
    })
  }

  concluir() {
    this.response.emit(true)
    this.dialogRef.close()
  }

  onRemove(grupo: GrupoUI, passageiro: PassageiroUI) {
    this.removePassengerLocal(grupo, passageiro.id)
  }

  openMoverDialog(grupo: GrupoUI, passageiro: PassageiroUI) {
    const otherGroups = this.grupos.filter(g => g !== grupo)
    const ref = this.dialog.open(ModalMoverPassageiroComponent, {
      data: { passageiro, grupos: otherGroups },
      width: '480px',
      maxHeight: '80vh',
    })
    ref.afterClosed().subscribe((toGrupo: GrupoUI | null) => {
      if (!toGrupo) return
      this.movePassengerLocal(grupo, passageiro.id, toGrupo)
    })
  }

  criarItemProposta(passageiro: any) {
    const dialogRef = this.dialog.open(ItemProposalCreateFormComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '100vw',
      width: '100vw',
      maxHeight: '100vh',
      height: '100vh',
      panelClass: 'dialog-fullscreen',
      data: {
        idClient: this.data.idCliente,
        period: {
          dataInicio: this.data.dataInicio,
          dataFim: this.data.dataFim,
        },
      },
    })
    dialogRef.componentInstance.response.subscribe((response: any) => {
      if (!response) return
      const model = this.mapItemProposalModel(response)
      this.proposalItemService.createProposalItemWithItinerariesPostProposal(model).subscribe({
        next: () => this.snackbar.success('Item de proposta criado com sucesso!'),
        error: (err: any) => this.snackbar.errorRequest(err),
      })
    })
  }

  private mapItemProposalModel(response: any) {
    return {
      propostaId: this.data.propostaId,
      tarifarioId: response.tarifarioId || null,
      tipoTarifarioId: response.tipoTarifarioId || null,
      perimetroUrbano: response.perimetroUrbano || null,
      origemId: response.origemId || 0,
      destinoId: response.destinoId || 0,
      tipoServicoId: response.tipoServicoId || null,
      servicoId: response.servicoId || null,
      tipoVeiculoId: response.tipoVeiculoId || null,
      tipoMotoristaId: response.tipoMotoristaId || null,
      numPessoas: response.numPessoas || 0,
      numVeiculos: response.numVeiculos || 0,
      valorUnitario: this.parseVal(response.vendaUnitario),
      valorTaxa: this.parseVal(response.valorTaxa),
      valorVenda: this.parseVal(response.vendaSemTaxa),
      valorComTaxa: this.parseVal(response.venda),
      valorKmVenda: this.parseVal(response.saleKmExtra),
      valorHoraVenda: this.parseVal(response.saleHoraExtra),
      isPermuta: response.isPermuta ? 'S' : 'N',
      isComissiona: response.isComisionado ? 'S' : 'N',
      isCortesia: response.isCortesia ? 'S' : 'N',
      dataInicio: response.dataInicio ? moment(response.dataInicio, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('DD/MM/YYYY') : null,
      horaInicio: this.toHHMMSS(response.horaInicio || ''),
      dataFinal: response.dataFinal ? moment(response.dataFinal, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('DD/MM/YYYY') : null,
      horaFinal: this.toHHMMSS(response.horaFinal || ''),
      taxa: Number(response.taxa) || 0,
      divisor: Number(response.divisor) || 0,
      comissao: Number(response.comissao) || 0,
      tipoDivisor: response.tipoComissao || null,
      observacao: response.observacao || '',
      itinerarios: (response.intinerarios || []).map((iti: any) => ({
        localParada: iti.localParada || '',
        cep: iti.cep || '',
        logradouro: iti.logradouro || '',
        numero: iti.numero || '',
        bairro: iti.bairro || '',
        complemento: iti.complemento || '',
        descricao: iti.descricao || '',
        cidade: iti.cidade || '',
        estado: iti.estado || '',
        latitude: iti.latitude || 0,
        longitude: iti.longitude || 0,
        trecho: iti.trecho || '',
        areaTarifario: iti.areaTarifario || '',
      })),
    }
  }

  private toHHMMSS(hora: string): string {
    if (!hora) return null as any
    const parts = hora.split(':')
    return parts.length === 3 ? hora : `${hora}:00`
  }

  private parseVal(value: any): number {
    if (typeof value === 'number') return value
    if (typeof value === 'string') {
      const n = parseFloat(value.replaceAll('.', '').replaceAll(',', '.'))
      return isNaN(n) ? 0 : n
    }
    return 0
  }

  private movePassengerLocal(from: GrupoUI, passageiroId: string, to: GrupoUI) {
    // eslint-disable-next-line eqeqeq
    const p = from.passageiros.find(p => p.id == passageiroId)
    if (!p) return

    // eslint-disable-next-line eqeqeq
    from.passageiros = from.passageiros.filter(p => p.id != passageiroId)
    to.passageiros = [...to.passageiros, { ...p }]
    from.totalPassageiros = from.passageiros.length
    to.totalPassageiros = to.passageiros.length
  }

  private removePassengerLocal(from: GrupoUI, passageiroId: string) {
    // eslint-disable-next-line eqeqeq
    const p = from.passageiros.find(p => p.id == passageiroId)
    if (!p) return

    // eslint-disable-next-line eqeqeq
    from.passageiros = from.passageiros.filter(p => p.id != passageiroId)
    this.semCorrespondencia = [...this.semCorrespondencia, { id: p.id, nome: p.nome, hora: p.hora, motivo: 'Removido manualmente' }]
    from.totalPassageiros = from.passageiros.length
  }

  close() {
    this.dialogRef.close()
  }
}
